/**
 * External Imports
 */
import { useEffect, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from 'react-oidc-context';
import { connect, useDispatch } from 'react-redux';

/**
 * Internal Imports
 */
import Header from '../../components/header';
import Loading from '../../components/shared/Loading';
import Login from '../login/Login';

import { RootState } from '../../redux/store';
import {
  openModal,
  setModalType,
  setModalData,
  setUserSection
} from '../../redux/actions';
import { useLazyGetIssueByIdQuery } from '../../redux/apis/Tasks.api';

import { useUserRolesRef } from '../../services/UserRoles/userRoles';
import { handleLastPath } from '../../utils/lastPath';

/**
 * Global Type Definition Imports
 */
import { IFacility, Task } from '../../config/interfaces';

/**
 * Constant Imports
 */
import serviceCentralCategories from '../../constants/serviceCentralCategories.json';

interface RouteProps {
  component?: JSX.Element;
  authUserRoles: Array<string>;
  facilities: IFacility[];
  userFacilityId: string;
}

const ProtectedRoute = ({
  component,
  authUserRoles,
  facilities,
  userFacilityId
}: RouteProps) => {
  const { isAuthenticated } = useAuth();

  const dispatch = useDispatch();

  const { pathname } = useLocation();
  const navigate = useNavigate();

  const {
    isEnterpriseAdmin,
    isNonSupportRounder,
    isCNEdRounder,
    isIlobRounder
  } = useUserRolesRef();

  const urlParams = new URLSearchParams(window.location.search);
  const code = urlParams.get('code');

  const storedDeepLinkTaskId =
    pathname.includes('task/complete') && window.sessionStorage.getItem('task');

  const [
    getIssueById,
    {
      data: issueByIdData = {} as Task
      // isError: isGetIssueByIdError,
      // isFetching: isGetIssueByIdFetching,
      // isLoading: isGetIssueByIdLoading
    }
  ] = useLazyGetIssueByIdQuery();

  const userFacility = useMemo(
    () =>
      facilities.filter(facility => facility.facilityId === userFacilityId)
        .length > 0
        ? facilities.filter(
            facility => facility.facilityId === userFacilityId
          )[0]
        : facilities[0],
    [facilities, userFacilityId]
  );

  // render login component when not prod environment else loading component
  const Fallback = useMemo(() => {
    if (
      code ||
      process.env.REACT_APP_ENV === 'production' ||
      pathname.includes('task/complete')
    ) {
      return <Loading label="api" />;
    }
    return <Login />;
  }, [code, pathname]);

  useEffect(() => {
    // If complete task from user email, fetch task for modal
    if (
      storedDeepLinkTaskId &&
      isAuthenticated &&
      userFacility &&
      authUserRoles
    ) {
      // if user is not a valid rounding type do not show issue data
      if (isEnterpriseAdmin || isNonSupportRounder || isCNEdRounder) {
        getIssueById(storedDeepLinkTaskId);
      } else {
        navigate('/404');
      }
      window.sessionStorage.removeItem('task');
    }
    if (isAuthenticated && userFacility && code && authUserRoles) {
      handleLastPath(authUserRoles, navigate);
    }
  }, [authUserRoles, userFacility]);

  useEffect(() => {
    // get task data and set modal data
    if (Object.keys(issueByIdData).length > 0) {
      if (
        serviceCentralCategories.includes(issueByIdData.category) ||
        serviceCentralCategories.includes(issueByIdData.taskSubtype)
      ) {
        dispatch(setModalType('View Issue'));
      } else {
        dispatch(setModalType('Edit Issue'));
      }
      dispatch(openModal());
      dispatch(setModalData(issueByIdData));
      dispatch(setUserSection(issueByIdData.roundingType));
    }
  }, [issueByIdData]);

  /**
   * Check if user is logged in already, else redirect to login page.
   */
  return isAuthenticated && (userFacility || isIlobRounder) ? (
    <div>
      <Header
        facilityTitle={!isIlobRounder ? userFacility.value : ''}
        divisionTitle={!isIlobRounder ? userFacility.division : ''}
      />
      {component}
    </div>
  ) : (
    Fallback
  );
};

const mapReduxStateToProps = (state: RootState) => ({
  authUserRoles: state.AuthorizedUser.authorizedUser.roles,
  facilities: state.ConfigReducer.userAccessibleFacilities,
  userFacilityId: state.AuthorizedUser.authorizedUser.facilityId
});

export default connect(mapReduxStateToProps)(ProtectedRoute);
